import { Box, Stack, Text } from "@mantine/core";
import React, { useMemo } from "react";
import type { MetricPercentile } from "../utils/ReadingRiskUtils";

interface Props {
  data: MetricPercentile;
  statusColor: "green" | "amber" | "red";
  label?: string;
}

const SVG_WIDTH = 260;
const SVG_HEIGHT = 80;
const CURVE_Y_TOP = 8;
const CURVE_Y_BOTTOM = 68;
const CURVE_X_PAD = 20;

const STATUS_COLOR_MAP = {
  green: {
    fill: "var(--mantine-color-green-1)",
    marker: "var(--mantine-color-green-6)",
    label: "var(--mantine-color-green-7)",
  },
  amber: {
    fill: "var(--mantine-color-yellow-1)",
    marker: "var(--mantine-color-yellow-6)",
    label: "var(--mantine-color-yellow-7)",
  },
  red: {
    fill: "var(--mantine-color-red-1)",
    marker: "var(--mantine-color-red-6)",
    label: "var(--mantine-color-red-7)",
  },
} satisfies Record<Props["statusColor"], { fill: string; marker: string; label: string }>;

function bellY(z: number): number {
  const pdf = Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
  const maxPdf = 1 / Math.sqrt(2 * Math.PI);
  const normalized = pdf / maxPdf;
  return CURVE_Y_BOTTOM - normalized * (CURVE_Y_BOTTOM - CURVE_Y_TOP);
}

function zToX(z: number): number {
  const clamped = Math.max(-3, Math.min(3, z));
  return CURVE_X_PAD + ((clamped + 3) / 6) * (SVG_WIDTH - 2 * CURVE_X_PAD);
}

export const BellCurvePercentile: React.FC<Props> = ({
  data,
  statusColor,
  label = "Performance",
}) => {
  const color = STATUS_COLOR_MAP[statusColor];

  const curvePath = useMemo(() => {
    const points: string[] = [];
    for (let i = 0; i <= 60; i++) {
      const z = -3 + (i / 60) * 6;
      const x = zToX(z);
      const y = bellY(z);
      points.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
    }
    return points.join(" ");
  }, []);

  const fillPath = useMemo(() => {
    const steps = 60;
    const childZ = Math.max(-3, Math.min(3, data.zScore));
    const maxI = Math.round(((childZ + 3) / 6) * steps);

    const points: string[] = [`M${zToX(-3).toFixed(1)},${CURVE_Y_BOTTOM}`];
    for (let i = 0; i <= maxI; i++) {
      const z = -3 + (i / steps) * 6;
      points.push(`L${zToX(z).toFixed(1)},${bellY(z).toFixed(1)}`);
    }
    const endZ = -3 + (maxI / steps) * 6;
    points.push(`L${zToX(endZ).toFixed(1)},${CURVE_Y_BOTTOM}`);
    points.push("Z");
    return points.join(" ");
  }, [data.zScore]);

  const markerX = zToX(data.zScore);
  const markerY = bellY(data.zScore);

  const ordinalSuffix = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return (
    <Stack gap={4} align="center">
      <svg
        width={SVG_WIDTH}
        height={SVG_HEIGHT}
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        style={{ overflow: "visible" }}
      >
        <line
          x1={CURVE_X_PAD}
          y1={CURVE_Y_BOTTOM}
          x2={SVG_WIDTH - CURVE_X_PAD}
          y2={CURVE_Y_BOTTOM}
          stroke="var(--mantine-color-gray-3)"
          strokeWidth={1}
        />

        <path d={fillPath} fill={color.fill} opacity={0.6} />

        <path
          d={curvePath}
          fill="none"
          stroke="var(--mantine-color-gray-5)"
          strokeWidth={2}
          strokeLinecap="round"
        />

        <circle
          cx={markerX}
          cy={markerY}
          r={5}
          fill={color.marker}
          stroke="white"
          strokeWidth={2}
        />

        <text
          x={markerX}
          y={markerY - 10}
          textAnchor="middle"
          fill={color.label}
          fontSize={11}
          fontWeight={700}
        >
          {ordinalSuffix(data.percentile)}
        </text>

        <text
          x={CURVE_X_PAD}
          y={CURVE_Y_BOTTOM + 12}
          textAnchor="start"
          fill="var(--mantine-color-gray-5)"
          fontSize={9}
        >
          Lower
        </text>
        <text
          x={SVG_WIDTH - CURVE_X_PAD}
          y={CURVE_Y_BOTTOM + 12}
          textAnchor="end"
          fill="var(--mantine-color-gray-5)"
          fontSize={9}
        >
          Higher
        </text>
      </svg>
      <Box>
        <Text size="xs" c="dimmed" ta="center" fw={500}>
          {label}: {ordinalSuffix(data.percentile)} percentile
        </Text>
        <Text size="10px" c="dimmed" ta="center">
          Mean {Math.round(data.populationMean)}, SD {Math.round(data.populationSd)}
        </Text>
      </Box>
    </Stack>
  );
};
